import { Form, Input } from "@ant-design/react-native";
import { Head } from "@shared/components/head";
import { Text } from "@shared/components/text";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TouchableOpacity, View, Keyboard } from "react-native";
import Button from "@shared/components/md-design-system/Button/Button";
import { CustomCheckbox } from "@shared/components/md-design-system/Checkbox/CustomCheckbox";

const { TextArea } = Input;

export interface IDrugIntakeSubmitForm {
    selectedOption: {value: string | null, text: string | null};
    notes?: string;
}

export const DrugIntakeSubmitForm = ({handleSubmit}: {handleSubmit: (data: IDrugIntakeSubmitForm) => void}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [selectedOption, setSelectedOption] = useState<{value: string | null, text: string | null} | null>();
    const [notes, setNotes] = useState<string | null>(null);

    const handleRowPress = (value: string, text: string) => {
      const isCurrentlySelected = selectedOption?.value === value;
      if (isCurrentlySelected) {
        setSelectedOption(null);
        form.setFieldsValue({ selectedOption: {value: null, text: null} });
        setNotes(null);
        form.setFieldsValue({ notes: null });
      } else {
        setSelectedOption({value, text});
        form.setFieldsValue({ selectedOption: {value, text} });
        if (value !== "3") {
          setNotes(null);
          form.setFieldsValue({ notes: null });
        }
      }
    };

    const handleNotesChange = (value: string) => {
      setNotes(value);
      form.setFieldsValue({ notes: value });
    };

    const isButtonDisabled = () => {
      if (!selectedOption) return true;
      if (selectedOption?.value === "3" && (!notes || notes.trim() === "")) return true;
      return false;
    };

    const handleScreenPress = () => {
      Keyboard.dismiss();
    };

    return (
      <TouchableOpacity
        activeOpacity={1}
        onPress={handleScreenPress}
        className="flex-1 flex-col justify-between"
      >
        <View className="flex-col gap-6">
          <Head type="h2">{t("drugs.notTaken.question")}</Head>
          <Form form={form}>
            <View className="flex-col gap-4 bg-white">
              <Text>{t("drugs.notTaken.selectOneOption")}</Text>

              <View>
                <Form.Item name="selectedOption" className="bg-white" noStyle>
                <View className='flex-col gap-2'>
                  <TouchableOpacity
                    className="flex-row items-center justify-between gap-2 p-4 border border-gray-300 rounded-lg"
                    onPress={() => handleRowPress("1", t("drugs.notTaken.answers.runOutOfMeds"))}
                  >
                    <Text>{t("drugs.notTaken.answers.runOutOfMeds")}</Text>
                    <CustomCheckbox
                      checked={selectedOption?.value === "1"}
                      onChange={() => handleRowPress("1", t("drugs.notTaken.answers.runOutOfMeds"))}
                    />
                  </TouchableOpacity>

                  <TouchableOpacity
                    className="flex-row items-center justify-between gap-2 p-4 border border-gray-300 rounded-lg"
                    onPress={() => handleRowPress("2", t("drugs.notTaken.answers.gotSideEffects"))}
                  >
                    <Text>{t("drugs.notTaken.answers.gotSideEffects")}</Text>
                    <CustomCheckbox
                      checked={selectedOption?.value === "2"}
                      onChange={() => handleRowPress("2", t("drugs.notTaken.answers.gotSideEffects"))}
                    />
                  </TouchableOpacity>

                  <TouchableOpacity
                    className="flex-row items-center justify-between gap-2 p-4 border border-gray-300 rounded-lg"
                    onPress={() => handleRowPress("3", t("drugs.notTaken.answers.otherReason"))}
                  >
                    <Text>{t("drugs.notTaken.answers.otherReason")}</Text>
                    <CustomCheckbox
                      checked={selectedOption?.value === "3"}
                      onChange={() => handleRowPress("3", t("drugs.notTaken.answers.otherReason"))}
                    />
                  </TouchableOpacity>
                </View>
                </Form.Item>
              </View>

              {selectedOption?.value === "3" && (
                <Form.Item name="notes"  noStyle>
                  <View className="flex-col gap-2">
                    <Text className="font-semibold">{t("drugs.reminder.note")}</Text>
                    <View className="border border-gray-300 rounded-lg p-3">
                      <TextArea
                        rows={10}
                        onChangeText={handleNotesChange}
                        style={{ height: 150 }}
                        placeholder={t("drugs.reminder.notePlaceholder")}
                      />
                    </View>
                  </View>
                </Form.Item>
              )}
            </View>
          </Form>
        </View>

        <View className="mt-6">
          <Button onPress={() => handleSubmit(form.getFieldsValue())} disabled={isButtonDisabled()}>
            {t("buttons.save")}
          </Button>
        </View>
      </TouchableOpacity>
    );
  };